import React, { useState } from 'react'
import { Button, Card, Flex } from 'antd'
import { useTranslation } from 'react-i18next'
import type { SeedInventory } from '../SeedInventoryTable'
import styled from 'styled-components'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import {
  drawerGrowerDetailsCardStyle,
  drawerInventoryNotesCardStyle,
  drawerRowStyle,
  drawerSeedInventoryCardStyle,
  labelStyle,
  valueStyle,
} from '../../styles'
import { formatMt, formatMtAsNegative } from '../../formatMt'
import SeedInventoryNotes from './SeedInventoryNotes'

// Styled component for Pedigree label
const PedigreeLabel = styled.div<{ backgroundColor: string }>`
  display: flex;
  height: 22px;
  padding: 0 12px;
  align-items: center;
  gap: 8px;
  border-radius: 12px;
  background: ${(props) => props.backgroundColor};
  color: white;
  font-size: 12px;
  width: fit-content;
`

// Function to get background color for each pedigree type
const getPedigreeBackgroundColor = (pedigree: SeedPedigree): string => {
  switch (pedigree) {
    case SeedPedigree.Certified:
      return '#20B02E'
    case SeedPedigree.Registered:
      return '#5C3700'
    case SeedPedigree.Foundation:
      return '#FFAB2E'
    case SeedPedigree.Select:
      return '#037BFF'
    case SeedPedigree.Breeder:
      return '#776D6D'
    default:
      return '#776D6D'
  }
}

function LabelValueRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Flex justify="space-between" align="center" style={drawerRowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value}</span>
    </Flex>
  )
}

function LabelValueRowWithUnits({ label, value, units }: { label: string; value: React.ReactNode; units: string }) {
  return (
    <Flex justify="space-between" align="center" style={drawerRowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>
        {value} {units}
      </span>
    </Flex>
  )
}

const CARD_ROW_GAP_PX = 16

function LabelPedigreeRow({ label, pedigree }: { label: string; pedigree: SeedPedigree }) {
  return (
    <Flex justify="space-between" align="center" style={drawerRowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>
        <PedigreeLabel backgroundColor={getPedigreeBackgroundColor(pedigree)}>{pedigree}</PedigreeLabel>
      </span>
    </Flex>
  )
}

type SeedInventoryDetailsTabProps = {
  record?: SeedInventory | null
}

const SeedInventoryDetailsTab: React.FC<SeedInventoryDetailsTabProps> = ({ record }) => {
  const { t } = useTranslation(['seedTracker', 'notes'])
  const [isAddingNote, setIsAddingNote] = useState(false)

  const openAddNote = () => {
    setIsAddingNote(true)
  }

  const closeAddNote = () => {
    setIsAddingNote(false)
  }

  return (
    <Flex vertical gap="middle" style={drawerRowStyle}>
      <Flex gap={CARD_ROW_GAP_PX} style={drawerRowStyle}>
        <Card
          title={t('seedTracker:drawerPanels.seedInventory')}
          variant="borderless"
          style={drawerSeedInventoryCardStyle}
        >
          <Flex vertical gap="small">
            {record && (
              <>
                <LabelValueRow
                  label={t('seedTracker:seedInventoryFields.cropCertificateId')}
                  value={record.cropCertificateId}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.available')}
                  value={formatMt(record.availableMT)}
                  units={t('seedTracker:seedInventoryFields.units.mt')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.pendingSale')}
                  value={formatMtAsNegative(record.pendingSaleMT)}
                  units={t('seedTracker:seedInventoryFields.units.mt')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.disposed')}
                  value={formatMtAsNegative(record.disposedMT)}
                  units={t('seedTracker:seedInventoryFields.units.mt')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.onHand')}
                  value={formatMt(record.onHandMT)}
                  units={t('seedTracker:seedInventoryFields.units.mt')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.cleanYieldActual')}
                  value={formatMt(record.cleanYieldActualMT)}
                  units={t('seedTracker:seedInventoryFields.units.mt')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.cleanYieldEstimate')}
                  value={formatMt(record.cleanYieldEstimateMT)}
                  units={t('seedTracker:seedInventoryFields.units.mt')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.grossYieldActual')}
                  value={formatMt(record.grossYieldActualBU)}
                  units={t('seedTracker:seedInventoryFields.units.bu')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.grossYieldEstimate')}
                  value={formatMt(record.grossYieldEstimateBU)}
                  units={t('seedTracker:seedInventoryFields.units.bu')}
                />
              </>
            )}
          </Flex>
        </Card>
        <Card
          title={t('seedTracker:drawerPanels.seedInventoryQuality')}
          variant="borderless"
          style={drawerSeedInventoryCardStyle}
        >
          <Flex vertical gap="small">
            {record && (
              <>
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.initialGermActual')}
                  value={record.initialGermActual}
                  units={t('seedTracker:seedInventoryFields.units.percentage')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.initialGermEstimate')}
                  value={record.initialGermEstimate}
                  units={t('seedTracker:seedInventoryFields.units.percentage')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.finalGermActual')}
                  value={record.finalGermActual}
                  units={t('seedTracker:seedInventoryFields.units.percentage')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.finalGermEstimate')}
                  value={record.finalGermEstimate}
                  units={t('seedTracker:seedInventoryFields.units.percentage')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.diseasedActual')}
                  value={record.diseasedActual}
                  units={t('seedTracker:seedInventoryFields.units.percentage')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.diseasedEstimate')}
                  value={record.diseasedEstimate}
                  units={t('seedTracker:seedInventoryFields.units.percentage')}
                />
              </>
            )}
          </Flex>
        </Card>
        <Card
          title={t('seedTracker:drawerPanels.seedingIntentions')}
          variant="borderless"
          style={drawerSeedInventoryCardStyle}
        >
          <Flex vertical gap="small">
            {record && (
              <>
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.plannedSeededArea')}
                  value={record.plannedSeededArea}
                  units={t('seedTracker:seedInventoryFields.units.acres')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.currentSeededAreaActual')}
                  value={record.currentSeededAreaActual}
                  units={t('seedTracker:seedInventoryFields.units.acres')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.currentSeededAreaEstimate')}
                  value={record.currentSeededAreaEstimate}
                  units={t('seedTracker:seedInventoryFields.units.acres')}
                />
                <LabelPedigreeRow
                  label={t('seedTracker:seedInventoryFields.seededPedigree')}
                  pedigree={record.seededPedigree}
                />
                <LabelPedigreeRow
                  label={t('seedTracker:seedInventoryFields.projectedPedigree')}
                  pedigree={record.projectedPedigree}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.projectedYieldActual')}
                  value={formatMt(record.projectedYieldActualBU)}
                  units={t('seedTracker:seedInventoryFields.units.bu')}
                />
                <LabelValueRowWithUnits
                  label={t('seedTracker:seedInventoryFields.projectedYieldEstimate')}
                  value={formatMt(record.projectedYieldEstimateBU)}
                  units={t('seedTracker:seedInventoryFields.units.bu')}
                />
              </>
            )}
          </Flex>
        </Card>
      </Flex>
      <Flex gap={CARD_ROW_GAP_PX} style={drawerRowStyle}>
        <Card
          title={t('seedTracker:drawerPanels.growerDetails')}
          variant="borderless"
          style={drawerGrowerDetailsCardStyle(CARD_ROW_GAP_PX)}
        >
          <Flex vertical gap="small">
            {record && (
              <>
                <LabelValueRow label={t('seedTracker:detail.growerFields.grower')} value={record.grower} />
                {/* <LabelValueRow label={t('seedTracker:detail.growerFields.location')} value={record.location} /> */}
                <LabelValueRow label={t('seedTracker:detail.growerFields.region')} value={record.region} />
                <LabelValueRow label={t('seedTracker:detail.growerFields.contactName')} value={record.contactName} />
                <LabelValueRow
                  label={t('seedTracker:detail.growerFields.contactNumber')}
                  value={record.contactNumber}
                />
                <LabelValueRow label={t('seedTracker:detail.growerFields.contactEmail')} value={record.contactEmail} />
              </>
            )}
          </Flex>
        </Card>
        <Card
          title={t('seedTracker:drawerPanels.inventoryNotes')}
          extra={!isAddingNote ? <Button onClick={openAddNote}>{t('notes:form.add', 'Add note')}</Button> : null}
          variant="borderless"
          style={drawerInventoryNotesCardStyle(CARD_ROW_GAP_PX)}
        >
          <SeedInventoryNotes
            record={record}
            isAddingNote={isAddingNote}
            onCancelAddNote={closeAddNote}
            onSaveAddNote={closeAddNote}
          />
        </Card>
      </Flex>
    </Flex>
  )
}

export default SeedInventoryDetailsTab
